import { Button, Link, Typography } from "@mui/material";
import { GridAlignment, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { StringCell } from "../../components/DataGrid/DataGrid.styles";
import StatusLabel from "../../components/StatusLabel/StatusLabel";
import { AppContext } from "../../core/context/appContextProvider";
import RoutingPaths from "../../core/routing/routingPaths";
import useRole from "../../core/routing/useRole";
import { updateDistributionsStatus } from "../../services/distributions.service";
import { useDistrbutionsEffect } from "../../services/hooks/useDistributionsEffect/useDistrbutionsEffect.hooks";
import { M_DASH_UNICODE } from "../../utils/constants/constants";
import {
  DISPLAY_DATE_FORMAT,
  DISPLAY_TIME,
} from "../../utils/constants/constants";
import { GENERIC_ERROR_MESSAGE } from "../../utils/constants/text.constants";
import { capitalize } from "../../utils/helpers/capitalize";
import { DateTimeFormat } from "../../utils/helpers/format.helper";
import {
  Distributions,
  DistributionsFilter,
  Statuses,
} from "../../utils/types/distributions.type";
import { CustomType, DataGridColDef } from "../../utils/types/listItems";
import { ScopeRole } from "../../utils/types/user.type";
import { legalTypeListResponse } from "../fund/fundDetails/__test__/FundDetails.helper";
import { DistributionDetailsTabs } from "./DistributionDetails/Constants";
import { TimeText } from "./Distributions.styles";

const StatusType = {
  published: "green",
  reviewing: "yellow",
  inactive: "red",
  editing: "aqua",
};

const STATUS_FILTER = [
  {
    id: "published",
    name: "Published",
  },
  {
    id: "reviewing",
    name: "Reviewing",
  },
  {
    id: "inactive",
    name: "Inactive",
  },
  {
    id: "editing",
    name: "Editing",
  },
];

const defaultHeaderList: DataGridColDef[] = [
  {
    field: "fundName",
    headerName: "Fund Name",
    hide: false,
    index: 2,
    type: "string",
    align: "left" as GridAlignment,
    width: 300,
  },
  {
    field: "createdOn",
    headerName: "Date Created",
    hide: false,
    index: 3,
    renderCell: (params) => {
      const date = params?.row?.createdOn ?? "";

      if (!date) {
        return (
          <StringCell>
            <Typography variant="body2">{M_DASH_UNICODE}</Typography>
          </StringCell>
        );
      }
      const formattedData = DateTimeFormat.shortDate(new Date(date));

      return (
        <StringCell>
          <Typography variant="body2">{formattedData}</Typography>
        </StringCell>
      );
    },
    align: "left" as GridAlignment,
    width: 200,
  },
  {
    field: "status",
    headerName: "Status",
    hide: false,
    index: 4,
    width: 150,
    filterable: true,
    renderCell: (params: GridRenderCellParams) => {
      const status = params?.row?.status?.toLowerCase();
      const color = StatusType[status as keyof typeof StatusType];

      return (
        <StringCell>
          {status ? (
            <StatusLabel
              color={color}
              isUpperCase={false}
              label={capitalize(status ?? M_DASH_UNICODE)}
            />
          ) : (
            M_DASH_UNICODE
          )}
        </StringCell>
      );
    },
    type: "string",
    align: "left" as GridAlignment,
    inlineFilter: true,
    inlineFilterName: DistributionsFilter.Statuses,
    inlineFilterIDField: "id",
    inlineFilterLabelField: "name",
    inlineFilterOptions: STATUS_FILTER,
    inlineFilterSelected: STATUS_FILTER?.map((status) => {
      if (status.name !== "Inactive") {
        return status.id;
      }
      return "";
    }),
  },
];

const ACTION_HEADER = {
  field: "action",
  headerName: " ",
  hide: false,
  hideable: false,
  index: 5,
  type: "action",
  customType: CustomType.Action,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  disableReorder: true,
  width: 100,
};

const NAMEHEADER = {
  field: "name",
  headerName: "Name",
  hide: false,
  index: 1,
  type: "string",
  align: "left" as GridAlignment,
  width: 300,
};

export const useDistribution = () => {
  const [list, setList] = useState<Distributions[] | undefined>();
  const [statusUpdateDistributions, setStatusUpdateDistributions] = useState<
    Distributions | undefined
  >();
  const [loading, setLoading] = useState<boolean>(false);
  const [openDistributionSetUp, setOpenDistributionSetUp] = useState<
    string | undefined
  >(undefined);
  const [headerList, setHeaderList] =
    useState<Array<DataGridColDef>>(defaultHeaderList);
  const [activeHeaderFields, setActiveHeaderFields] = useState(6);
  const { informationAlert } = useContext(AppContext);
  const history = useHistory();
  const {
    distributions: distributionsList,
    fetchDistributions,
    loading: loadingDistributions,
    updateDistributions,
  } = useDistrbutionsEffect();

  const { hasRole: isSuperAdminOrClientAdmin } = useRole([
    ScopeRole.SUPER_ADMIN,
    ScopeRole.ARK_CLIENT_ADMIN,
    ScopeRole.BASIC_ADMIN,
  ]);

  const toggleStatus = useCallback((distributions: Distributions) => {
    setStatusUpdateDistributions(distributions);
  }, []);
  const updateStatus = async () => {
    setStatusUpdateDistributions(undefined);
    if (!statusUpdateDistributions) {
      return;
    }
    const { id, status: currentStatus } = statusUpdateDistributions;

    try {
      setLoading(true);

      let updatedStatus: string = "";

      switch (currentStatus?.toUpperCase()) {
        case "PUBLISHED":
          updatedStatus = "EDITING";
          break;
        case "REVIEWING":
          updatedStatus = "INACTIVE";
          break;
        case "EDITING":
          updatedStatus = "INACTIVE";
          break;
        default:
          updatedStatus = "EDITING";
          break;
      }

      await updateDistributionsStatus(id!, updatedStatus);

      if (list) {
        const updatedDistributions = list.map((distributions) => {
          if (distributions.id === id) {
            return {
              ...distributions,
              status: updatedStatus,
            };
          } else return distributions;
        });

        updateDistributions(updatedDistributions);
        setLoading(false);
      }
    } catch (error) {
      informationAlert(GENERIC_ERROR_MESSAGE, "error");
    }
  };

  useEffect(() => {
    const newHeaderList = [
      {
        ...NAMEHEADER,
        renderCell: (params: any) => {
          return (
            <Link
              id="distribution_setup_link"
              underline="none"
              component="button"
              onClick={(event) => {
                setOpenDistributionSetUp(params.row.id);
                // history.push(
                //   `${RoutingPaths.Distributions}/${params.row.id}/${DistributionDetailsTabs.DistributionSetup}`
                // );
              }}
            >
              {params.value}
            </Link>
          );
        },
      },
      ...defaultHeaderList,
      {
        ...ACTION_HEADER,
        renderCell: (params: GridRenderCellParams) => {
          const status = params?.row?.status?.toLowerCase();
          const displayUpdateStatus = status === "inactive" ? false : true;

          return (
            displayUpdateStatus &&
            isSuperAdminOrClientAdmin && (
              <Button
                id={`${params?.row?.id}_distributions`}
                variant="outlined"
                size="small"
                onClick={() => toggleStatus(params.row)}
              >
                <strong>{getStatus(status)}</strong>
              </Button>
            )
          );
        },
      },
    ];

    setHeaderList(newHeaderList);
  }, [toggleStatus]);

  const getSelectedFilters = useCallback(
    (currentHeaderList: DataGridColDef[]) => {
      return currentHeaderList?.reduce(
        (acc: Record<DistributionsFilter, string[] | undefined>, header) => {
          if (
            header.inlineFilter &&
            header.inlineFilterSelected &&
            header.inlineFilterSelected?.length > 0
          ) {
            return {
              ...acc,
              [header.inlineFilterName ?? ""]: header.inlineFilterSelected?.map(
                (item) => item.toLowerCase()
              ),
            };
          }
          return acc;
        },
        {
          [DistributionsFilter.Statuses]: undefined,
        }
      );
    },
    []
  );

  const handleConfirmSkiptoTransaction = () => {
    history.push(
      `${RoutingPaths.Distributions}/${openDistributionSetUp}/${DistributionDetailsTabs.AddTransactions}`
    );
  };

  const handleStartFromBeginning = () => {
    history.push(
      `${RoutingPaths.Distributions}/${openDistributionSetUp}/${DistributionDetailsTabs.DistributionSetup}`
    );
  };

  useEffect(() => {
    const { [DistributionsFilter.Statuses]: selectedStatuses } =
      getSelectedFilters(headerList);

    const currentList =
      distributionsList?.filter(({ status }) =>
        selectedStatuses?.includes(status?.toLowerCase() || "")
      ) ?? [];

    setList(currentList);
  }, [distributionsList, headerList]);

  const handleUpdateHeader = async (field: string) => {
    if (!headerList || headerList?.length === 0) {
      return;
    }

    const activeFields = headerList.filter(
      (header) => !header.hide && header?.type !== "action"
    );

    const updatedHeaders: Array<DataGridColDef> = headerList.map((header) => {
      return {
        ...header,
        hide:
          header.field === field && !(!header.hide && activeFields.length <= 1)
            ? !header.hide
            : header.hide,
      };
    });

    if (updatedHeaders) {
      await setHeaderList(updatedHeaders);
      const activeHeaders = headerList.filter((header) => !header.hide);

      await setActiveHeaderFields(activeHeaders.length - 1);
    }
  };

  const handleFilter = (
    filterName: DistributionsFilter,
    selected: string[]
  ) => {
    setHeaderList((prevHeaderList) =>
      prevHeaderList?.map((header) => {
        if (header.inlineFilterName === filterName) {
          return {
            ...header,
            inlineFilterSelected: selected,
          };
        }

        return header;
      })
    );
  };

  const discardStatusUpdate = () => {
    setStatusUpdateDistributions(undefined);
  };

  const discardOpenDistributionSetUp = () => {
    setOpenDistributionSetUp(undefined);
  };

  const getStatus = useCallback((status: string) => {
    switch (status.toLowerCase()) {
      case Statuses.PUBLISHED:
        return "Unpublish";
      case Statuses.INACTIVE:
        return "Activate";
      case Statuses.EDITING:
        return "Inactivate";
      case Statuses.REVIEWING:
        return "Inactivate";
      default:
        return "";
    }
    return "";
  }, []);

  const statusAction = useMemo(() => {
    return getStatus(statusUpdateDistributions?.status ?? "");
  }, [statusUpdateDistributions, getStatus]);

  return {
    headerList,
    loading: loadingDistributions,
    list,
    activeHeaderFields,
    handleUpdateHeader,
    handleFilter,
    fetchDistributions,
    statusUpdateDistributions,
    discardStatusUpdate,
    updateStatus,
    statusAction,
    openDistributionSetUp,
    handleConfirmSkiptoTransaction,
    handleStartFromBeginning,
    discardOpenDistributionSetUp,
    isSuperAdminOrClientAdmin,
  };
};
